import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';
import printTable from '../utils/printUtils';

function useIsMobile() {
  const [v, setV] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const h = () => setV(window.innerWidth < 640);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return v;
}

// ── Lifted outside OrderStatement so React never remounts it on re-render ────
function AiQtyCell({ product, mobile, forecast, hasOverride, override, aiLoading, onOverrideChange, onOverrideClear }) {
  if (!forecast?.recommendedOrderQty) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <span style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
          {aiLoading ? '⏳ Loading…' : '— No forecast yet'}
        </span>
        <span style={{ fontSize: '11px', color: '#cbd5e1' }}>Visit Dashboard to generate</span>
      </div>
    );
  }

  const conf      = forecast.confidence;
  const confColor = conf === 'high' ? '#16a34a' : conf === 'medium' ? '#ca8a04' : '#94a3b8';
  const confBg    = conf === 'high' ? '#f0fdf4' : conf === 'medium' ? '#fefce8' : '#f8fafc';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: mobile ? '18px' : '17px', fontWeight: '800', color: '#6366f1', letterSpacing: '-0.01em' }}>
          ✨ {forecast.recommendedOrderQty}
        </span>
        <span style={{ fontSize: '11px', fontWeight: '700', color: confColor, backgroundColor: confBg, padding: '2px 8px', borderRadius: '20px', border: `1px solid ${confColor}44` }}>
          {conf} confidence
        </span>
      </div>
      {forecast.reason && (
        <div style={{ fontSize: '11px', color: '#64748b', fontStyle: 'italic', lineHeight: 1.4 }}>
          {forecast.reason}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <input
          type="number"
          min="1"
          placeholder={`Override ${forecast.recommendedOrderQty}`}
          value={override}
          onChange={e => onOverrideChange(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
          style={{
            width: mobile ? '110px' : '100px',
            padding: '6px 10px',
            border: '1.5px solid #c7d2fe',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#1e293b',
            backgroundColor: '#ffffff',
            boxSizing: 'border-box',
            fontWeight: hasOverride ? '700' : '400',
          }}
          title="Override AI quantity"
        />
        {hasOverride && (
          <button
            onClick={onOverrideClear}
            style={{ padding: '6px 10px', backgroundColor: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '8px', color: '#6366f1', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}
          >
            ↺ Reset
          </button>
        )}
      </div>
    </div>
  );
}

// ── Helper to build AiQtyCell props — avoids repetition at 3 call sites ──────
function makeAiCellProps(product, aiForecasts, aiOverrides, aiLoading, setAiOverrides) {
  return {
    forecast:       aiForecasts[product.id],
    hasOverride:    aiOverrides[product.id] !== undefined,
    override:       aiOverrides[product.id] ?? '',
    aiLoading,
    onOverrideChange: (val) => setAiOverrides(prev => ({
      ...prev,
      [product.id]: val === '' ? undefined : val,
    })),
    onOverrideClear: () => setAiOverrides(prev => {
      const n = { ...prev }; delete n[product.id]; return n;
    }),
  };
}

export default function OrderStatement() {
  const { orgId } = useAuth();
  const isMobile  = useIsMobile();

  const [products,    setProducts]    = useState([]);
  const [filterGroup, setFilterGroup] = useState('All');
  const [search,      setSearch]      = useState('');
  const [aiForecasts, setAiForecasts] = useState({});
  const [aiLoading,   setAiLoading]   = useState(false);
  const [aiOverrides, setAiOverrides] = useState({});

  useEffect(() => {
    if (!orgId) return;
    const q = query(collection(db, 'products'), where('orgId', '==', orgId), orderBy('name'));
    return onSnapshot(q, snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [orgId]);

  useEffect(() => {
    if (!orgId || products.length === 0) return;
    const lowProducts = products.filter(isLow);
    if (lowProducts.length === 0) return;
    const fetchCachedForecasts = async () => {
      setAiLoading(true);
      const map = {};
      await Promise.allSettled(
        lowProducts.map(async (p) => {
          try {
            const snap = await getDoc(doc(db, 'forecasts', `${orgId}_${p.id}`));
            if (snap.exists()) {
              const data = snap.data();
              const ageMs = Date.now() - (data.cachedAt?.toMillis() || 0);
              if (ageMs < 24 * 60 * 60 * 1000 && data.result?.recommendedOrderQty) {
                map[p.id] = data.result;
              }
            }
          } catch { /* cache miss is non-fatal */ }
        })
      );
      setAiForecasts(map);
      setAiLoading(false);
    };
    fetchCachedForecasts();
  }, [orgId, products]);

  const isLow     = (p) => p.minStock != null ? p.stock < p.minStock : p.stock < p.moq;
  const threshold = (p) => p.minStock != null ? p.minStock : p.moq;
  const toOrder   = (p) => threshold(p) - p.stock;
  const urgency   = (p) => {
    if (p.stock === 0)                      return 'critical';
    if (p.stock < threshold(p) * 0.25)     return 'high';
    return 'normal';
  };
  const effectiveOrderQty = (p) => {
    if (aiOverrides[p.id] !== undefined)        return Number(aiOverrides[p.id]);
    if (aiForecasts[p.id]?.recommendedOrderQty) return aiForecasts[p.id].recommendedOrderQty;
    return toOrder(p);
  };

  const lowProducts  = products.filter(isLow);
  const groups       = ['All', ...new Set(lowProducts.map(p => p.group).filter(Boolean))];
  const filtered     = lowProducts
    .filter(p => filterGroup === 'All' || p.group === filterGroup)
    .filter(p => p.name?.toLowerCase().includes(search.toLowerCase()));
  const totalToOrder = filtered.reduce((sum, p) => sum + effectiveOrderQty(p), 0);
  const aiCount      = filtered.filter(p => aiForecasts[p.id]?.recommendedOrderQty).length;

  const exportToExcel = () => {
    const rows = filtered.map(p => ({
      'Product Name':    p.name,
      'Group':           p.group,
      'Current Stock':   p.stock,
      'Min Stock':       p.minStock ?? '(using MOQ)',
      'MOQ':             p.moq,
      'Min-based Qty':   toOrder(p),
      'AI Suggested Qty': aiForecasts[p.id]?.recommendedOrderQty ?? '—',
      'Final Order Qty': effectiveOrderQty(p),
      'AI Confidence':   aiForecasts[p.id]?.confidence ?? '—',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Order Statement');
    XLSX.writeFile(wb, 'order-statement.xlsx');
  };

  const printOrder = () => {
    const urgencyMap = {
      critical: { label: 'OUT OF STOCK', style: 'color:#dc2626;font-weight:700;' },
      high:     { label: 'CRITICAL',     style: 'color:#f97316;font-weight:700;' },
      normal:   { label: 'LOW',          style: 'color:#ca8a04;font-weight:700;' },
    };
    printTable({
      title: 'Order Statement',
      subtitle: `${filtered.length} products · Total to order: ${totalToOrder} units`,
      columns: ['Product Name', 'Group', 'Stock', 'Min-based Qty', 'AI Suggested', 'Final Qty', 'Urgency'],
      rows: filtered.map(p => {
        const u  = urgency(p);
        const ai = aiForecasts[p.id];
        return [
          p.name, p.group || '—',
          { value: p.stock,              style: 'color:#ef4444;font-weight:700;' },
          toOrder(p),
          { value: ai?.recommendedOrderQty ?? '—', style: ai ? 'color:#6366f1;font-weight:600;' : 'color:#94a3b8;' },
          { value: effectiveOrderQty(p), style: 'color:#f97316;font-weight:700;' },
          { value: urgencyMap[u].label,  style: urgencyMap[u].style },
        ];
      }),
      styles: { colWidths: ['22%', '10%', '8%', '12%', '12%', '12%', '14%'] },
    });
  };

  const ActionButtons = () => (
    <div style={{ display: 'flex', gap: '8px' }}>
      <button onClick={exportToExcel} style={isMobile ? m.actionBtn : styles.exportBtn}>⬇ {isMobile ? 'Export' : 'Export Excel'}</button>
      <button onClick={printOrder}    style={isMobile ? m.actionBtn : styles.exportBtn}>🖨️ Print</button>
    </div>
  );

  // ── Mobile product card ──────────────────────────────────────────────────────
  const MobileProductCard = ({ product }) => {
    const u        = urgency(product);
    const finalQty = effectiveOrderQty(product);
    const aiProps  = makeAiCellProps(product, aiForecasts, aiOverrides, aiLoading, setAiOverrides);
    return (
      <div style={m.card}>
        <div style={m.cardTopRow}>
          <span style={m.productName}>{product.name}</span>
          {u === 'critical' && <span style={styles.criticalBadge}>OUT OF STOCK</span>}
          {u === 'high'     && <span style={styles.highBadge}>CRITICAL</span>}
          {u === 'normal'   && <span style={styles.normalBadge}>LOW</span>}
        </div>
        {product.group && <div style={{ marginBottom: '10px' }}><span style={styles.groupBadge}>{product.group}</span></div>}
        <div style={m.statsGrid}>
          <div style={m.statBox}>
            <span style={m.statLabel}>Current</span>
            <span style={{ ...m.statValue, color: '#ef4444' }}>{product.stock}</span>
          </div>
          <div style={m.statBox}>
            <span style={m.statLabel}>Min Stock</span>
            <span style={m.statValue}>
              {product.minStock != null ? product.minStock : <span style={{ fontSize: '10px', color: '#94a3b8' }}>MOQ ({product.moq})</span>}
            </span>
          </div>
          <div style={m.statBox}>
            <span style={m.statLabel}>Min-based</span>
            <span style={m.statValue}>{toOrder(product)}</span>
          </div>
          <div style={{ ...m.statBox, backgroundColor: '#eef2ff', borderColor: '#c7d2fe' }}>
            <span style={m.statLabel}>Final Qty</span>
            <span style={{ ...m.statValue, color: '#6366f1' }}>{finalQty}</span>
          </div>
        </div>
        <div style={m.aiSection}>
          <span style={m.aiSectionLabel}>✨ AI Suggestion</span>
          <AiQtyCell product={product} mobile={true} {...aiProps} />
        </div>
      </div>
    );
  };

  // ── MOBILE LAYOUT ────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div>
        <div style={m.header}>
          <div>
            <h1 style={m.title}>Order Statement</h1>
            <p style={styles.subtitle}>
              {lowProducts.length === 0 ? 'All products are above minimum stock' : `${filtered.length} products need restocking`}
            </p>
          </div>
          {lowProducts.length > 0 && <ActionButtons />}
        </div>

        {lowProducts.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>✅</div>
            <p style={styles.emptyTitle}>All products are above minimum stock</p>
            <p style={styles.emptySubtitle}>No orders need to be placed right now</p>
          </div>
        ) : (
          <>
            <div style={m.summaryRow}>
              {[
                { label: 'To Restock',     value: lowProducts.length,                                     color: '#ef4444', bg: '#fef2f2' },
                { label: 'Units to Order', value: totalToOrder,                                            color: '#6366f1', bg: '#eef2ff' },
                { label: 'AI Forecasted',  value: aiLoading ? '…' : `${aiCount}/${filtered.length}`,      color: '#6366f1', bg: '#eef2ff' },
              ].map(card => (
                <div key={card.label} style={{ ...m.summaryCard, backgroundColor: card.bg }}>
                  <span style={{ ...m.summaryValue, color: card.color }}>{card.value}</span>
                  <span style={m.summaryLabel}>{card.label}</span>
                </div>
              ))}
            </div>

            {!aiLoading && aiCount > 0 && (
              <div style={m.aiBanner}>
                ✨ AI demand forecasts loaded for {aiCount} product{aiCount !== 1 ? 's' : ''} — quantities updated based on 14-day sales history. Override any value below.
              </div>
            )}
            {aiLoading && <div style={{ ...m.aiBanner, color: '#6366f1' }}>✨ Loading AI demand forecasts from cache…</div>}

            <input type="text" placeholder="Search products…" value={search} onChange={e => setSearch(e.target.value)} style={m.search} />

            {groups.length > 1 && (
              <div style={m.groupScrollRow}>
                {groups.map(g => (
                  <button key={g} onClick={() => setFilterGroup(g)} style={filterGroup === g ? styles.groupActive : styles.groupInactive}>{g}</button>
                ))}
              </div>
            )}

            {filtered.length === 0
              ? <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>No products match your filter</div>
              : filtered.map(product => <MobileProductCard key={product.id} product={product} />)
            }
          </>
        )}
      </div>
    );
  }

  // ── DESKTOP LAYOUT ───────────────────────────────────────────────────────────
  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Order Statement</h1>
          <p style={styles.subtitle}>
            {lowProducts.length === 0 ? 'All products are above minimum stock' : `${filtered.length} products need restocking`}
          </p>
        </div>
        {lowProducts.length > 0 && <ActionButtons />}
      </div>

      {lowProducts.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>✅</div>
          <p style={styles.emptyTitle}>All products are above minimum stock</p>
          <p style={styles.emptySubtitle}>No orders need to be placed right now</p>
        </div>
      ) : (
        <>
          <div style={styles.cards}>
            {[
              { label: 'Products to Restock', value: lowProducts.length,                          color: '#ef4444', bg: '#fef2f2' },
              { label: 'Total Units to Order', value: totalToOrder,                               color: '#6366f1', bg: '#eef2ff' },
              { label: 'Out of Stock',         value: products.filter(p => p.stock === 0).length, color: '#dc2626', bg: '#fef2f2' },
              { label: 'AI Forecasts Loaded',  value: aiLoading ? '…' : `${aiCount} / ${filtered.length}`, color: '#6366f1', bg: '#eef2ff' },
            ].map(card => (
              <div key={card.label} style={{ ...styles.card, backgroundColor: card.bg }}>
                <p style={{ ...styles.cardValue, color: card.color }}>{card.value}</p>
                <p style={styles.cardLabel}>{card.label}</p>
              </div>
            ))}
          </div>

          {!aiLoading && aiCount > 0 && (
            <div style={styles.aiBanner}>
              <span style={styles.aiBannerIcon}>✨</span>
              <span>AI demand forecasts loaded for <strong>{aiCount}</strong> product{aiCount !== 1 ? 's' : ''} using 14-day sales history. Quantities reflect 30-day demand coverage. Override any value in the table — final totals update instantly.</span>
            </div>
          )}
          {aiLoading && (
            <div style={{ ...styles.aiBanner, color: '#6366f1' }}>
              <span style={styles.aiBannerIcon}>✨</span>Loading AI demand forecasts from cache…
            </div>
          )}

          <div style={styles.filters}>
            <input type="text" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} style={styles.search} />
            <div style={styles.groupFilters}>
              {groups.map(g => (
                <button key={g} onClick={() => setFilterGroup(g)} style={filterGroup === g ? styles.groupActive : styles.groupInactive}>{g}</button>
              ))}
            </div>
          </div>

          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Product Name', 'Group', 'Current Stock', 'Min Stock', 'MOQ', 'Min-based Qty', '✨ AI Suggested', 'Final Qty', 'Urgency'].map(h => (
                    <th key={h} style={{ ...styles.th, ...(h.includes('AI') ? { color: '#6366f1', backgroundColor: '#eef2ff' } : {}) }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(product => {
                  const u        = urgency(product);
                  const finalQty = effectiveOrderQty(product);
                  const hasAI    = !!aiForecasts[product.id]?.recommendedOrderQty;
                  const aiProps  = makeAiCellProps(product, aiForecasts, aiOverrides, aiLoading, setAiOverrides);
                  return (
                    <tr key={product.id} style={styles.tr}>
                      <td style={styles.tdName}>{product.name}</td>
                      <td style={styles.td}><span style={styles.groupBadge}>{product.group}</span></td>
                      <td style={styles.td}><span style={styles.stockLow}>{product.stock}</span></td>
                      <td style={{ ...styles.td, color: '#64748b' }}>
                        {product.minStock != null ? product.minStock : <span style={{ color: '#94a3b8', fontSize: '12px' }}>using MOQ ({product.moq})</span>}
                      </td>
                      <td style={{ ...styles.td, color: '#64748b' }}>{product.moq}</td>
                      <td style={styles.td}><span style={styles.minBasedQty}>{toOrder(product)}</span></td>
                      <td style={{ ...styles.td, backgroundColor: '#fafbff' }}>
                        <AiQtyCell product={product} mobile={false} {...aiProps} />
                      </td>
                      <td style={styles.td}>
                        <span style={{ ...styles.orderQty, backgroundColor: hasAI ? '#eef2ff' : '#fff7ed', color: hasAI ? '#6366f1' : '#f97316' }}>
                          {finalQty}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {u === 'critical' && <span style={styles.criticalBadge}>OUT OF STOCK</span>}
                        {u === 'high'     && <span style={styles.highBadge}>CRITICAL</span>}
                        {u === 'normal'   && <span style={styles.normalBadge}>LOW</span>}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No products match your filter</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Mobile styles ─────────────────────────────────────────────────────────────
const m = {
  header:       { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px', gap: '12px' },
  title:        { fontSize: '20px', fontWeight: '700', color: '#1e293b', marginBottom: '2px' },
  actionBtn:    { padding: '8px 12px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', color: '#475569', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  summaryRow:   { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '14px' },
  summaryCard:  { borderRadius: '10px', padding: '10px 8px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center' },
  summaryValue: { fontSize: '22px', fontWeight: '800' },
  summaryLabel: { fontSize: '10px', color: '#64748b', fontWeight: '500', textAlign: 'center' },
  aiBanner:     { backgroundColor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: '#4f46e5', marginBottom: '12px' },
  search:       { width: '100%', boxSizing: 'border-box', padding: '10px 14px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', fontSize: '16px', marginBottom: '10px' },
  groupScrollRow: { display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '10px', marginBottom: '4px', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' },
  card:         { backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px', marginBottom: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  cardTopRow:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '8px' },
  productName:  { fontSize: '14px', fontWeight: '600', color: '#1e293b', flex: 1, lineHeight: 1.4 },
  statsGrid:    { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px', marginBottom: '10px' },
  statBox:      { backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 6px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' },
  statLabel:    { fontSize: '9px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'center' },
  statValue:    { fontSize: '15px', fontWeight: '700', color: '#1e293b' },
  aiSection:    { borderTop: '1px solid #f1f5f9', paddingTop: '10px' },
  aiSectionLabel: { fontSize: '11px', fontWeight: '700', color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '6px' },
};

// ── Desktop styles ────────────────────────────────────────────────────────────
const styles = {
  header:       { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  title:        { fontSize: '24px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' },
  subtitle:     { fontSize: '14px', color: '#64748b' },
  exportBtn:    { padding: '9px 18px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', color: '#475569', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  emptyState:   { textAlign: 'center', padding: '80px 24px', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  emptyIcon:    { fontSize: '48px', marginBottom: '16px' },
  emptyTitle:   { fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' },
  emptySubtitle:{ fontSize: '14px', color: '#64748b' },
  cards:        { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '16px' },
  card:         { borderRadius: '10px', padding: '18px 20px', border: '1px solid #e2e8f0' },
  cardValue:    { fontSize: '30px', fontWeight: '800', marginBottom: '4px' },
  cardLabel:    { fontSize: '13px', color: '#64748b' },
  aiBanner:     { display: 'flex', alignItems: 'flex-start', gap: '10px', backgroundColor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '10px', padding: '12px 16px', fontSize: '13px', color: '#4f46e5', marginBottom: '16px' },
  aiBannerIcon: { fontSize: '16px', flexShrink: 0 },
  filters:      { display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' },
  search:       { padding: '9px 14px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', fontSize: '14px', width: '240px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  groupFilters: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  groupActive:  { padding: '7px 16px', borderRadius: '20px', border: 'none', backgroundColor: '#3b82f6', color: '#fff', fontSize: '13px', cursor: 'pointer', fontWeight: '600', boxShadow: '0 2px 8px rgba(59,130,246,0.3)', whiteSpace: 'nowrap' },
  groupInactive:{ padding: '7px 16px', borderRadius: '20px', border: '1.5px solid #e2e8f0', backgroundColor: '#ffffff', color: '#64748b', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },
  tableWrapper: { backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  table:        { width: '100%', borderCollapse: 'collapse' },
  th:           { padding: '13px 16px', textAlign: 'left', color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', backgroundColor: '#f8fafc', whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0' },
  tr:           { borderBottom: '1px solid #f1f5f9' },
  tdName:       { padding: '14px 16px', color: '#1e293b', fontSize: '14px', fontWeight: '500' },
  td:           { padding: '14px 16px', color: '#374151', fontSize: '14px' },
  groupBadge:   { backgroundColor: '#eff6ff', color: '#3b82f6', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' },
  stockLow:     { color: '#ef4444', fontWeight: '700', fontSize: '15px', backgroundColor: '#fef2f2', padding: '3px 10px', borderRadius: '6px' },
  minBasedQty:  { color: '#94a3b8', fontWeight: '600', fontSize: '14px', backgroundColor: '#f8fafc', padding: '3px 10px', borderRadius: '6px' },
  orderQty:     { fontWeight: '700', fontSize: '15px', padding: '3px 10px', borderRadius: '6px' },
  criticalBadge:{ backgroundColor: '#fef2f2', color: '#dc2626', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.05em', border: '1px solid #fecaca' },
  highBadge:    { backgroundColor: '#fff7ed', color: '#f97316', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.05em', border: '1px solid #fed7aa' },
  normalBadge:  { backgroundColor: '#fef9c3', color: '#ca8a04', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.05em', border: '1px solid #fde047' },
};